import { cx, memberStyle, NEUTRAL_COLOR } from './color.js';
import styles from './EventCard.module.css';
import { Icon } from './Icon.js';
import { MemberChip } from './MemberChip.js';
import { Pictogram } from './Pictogram.js';

export interface EventCardMember {
  name: string;
  colorKey: string;
  icon?: string | null;
}

/** Teksten uit i18n (`design.eventCard`); ui-core kent zelf geen copy. */
export interface EventCardLabels {
  allDay: string;
  cancelled: string;
  everyone: string;
  whoDrives: string;
  /** Met `{name}`, bv. "{name} rijdt". */
  drives: string;
  repeat: string;
  moved: string;
  changed: string;
}

export interface EventCardProps {
  title: string;
  /** "15:30"; leeg bij een hele-dag-item. */
  time?: string | null;
  endTime?: string | null;
  allDay?: boolean;
  icon?: string | null;
  /** Restpunten: false = geen pictogram (volwassen context: geen pictogram per regel). */
  showIcon?: boolean;
  members?: ReadonlyArray<EventCardMember>;
  /** Totaal aantal leden in het huishouden: >= 4 of iedereen → gezinschip (§7.1). */
  householdSize?: number;
  location?: string | null;
  /** Rijder (brengen/halen) of 'missing' voor de rode "Wie rijdt?"-chip. */
  driver?: EventCardMember | 'missing' | null;
  /** Andere tekst naast de auto, bv. "Anne brengt · Baba haalt" (brengen en halen verschillen). */
  driverText?: string | null;
  repeat?: boolean;
  moved?: boolean;
  changed?: boolean;
  cancelled?: boolean;
  /** next = eerstvolgende (groter, rustige schaduw), past = voorbij (vervaagd). */
  tone?: 'default' | 'next' | 'past';
  size?: 'kiosk' | 'web';
  labels: EventCardLabels;
  onPress?: () => void;
  name?: string;
}

/**
 * Agenda-item (kiosk Vandaag-lijst, web Vandaag/Agenda). Links een verticale kleurstreep per lid,
 * dan tijd, pictogram, titel en de chips. Geen tabelgevoel: één rustige kaart per item.
 */
export function EventCard({
  title,
  time,
  endTime,
  allDay,
  icon,
  showIcon = true,
  members = [],
  householdSize,
  location,
  driver,
  driverText,
  repeat,
  moved,
  changed,
  cancelled,
  tone = 'default',
  size = 'web',
  labels,
  onPress,
  name,
}: EventCardProps) {
  const kiosk = size === 'kiosk';
  const everyone =
    members.length >= 4 || (householdSize !== undefined && members.length >= householdSize);
  // Fixer golf 6b r1: een huishouden van één (alleen) heeft geen "Iedereen" en geen eigen chip nodig.
  const alone = householdSize !== undefined && householdSize <= 1;
  const stripe = members.length > 0 ? members.slice(0, 6) : [];
  const body = (
    <>
      <span class={styles.stripe} aria-hidden="true">
        {stripe.length === 0 ? (
          <span class={styles.stripePart} />
        ) : (
          stripe.map((m, i) => (
            <span key={i} class={styles.stripePart} style={memberStyle(m.colorKey)} />
          ))
        )}
      </span>
      <span class={styles.time}>
        {allDay ? (
          <span class={styles.allDay}>{labels.allDay}</span>
        ) : (
          <>
            <span class={styles.start}>{time}</span>
            {endTime ? <span class={styles.end}>{endTime}</span> : null}
          </>
        )}
      </span>
      {showIcon ? (
        <span class={styles.picto}>
          <Pictogram icon={icon ?? null} label={title} size={kiosk ? 56 : 36} decorative />
        </span>
      ) : null}
      <span class={styles.main}>
        <span class={styles.titleRow}>
          <span class={cx(styles.title, cancelled && styles.struck)}>{title}</span>
          {repeat ? (
            <span class={styles.glyph} title={labels.repeat}>
              <Icon id="repeat" size={kiosk ? 24 : 16} label={labels.repeat} />
            </span>
          ) : null}
          {moved ? (
            <span class={styles.glyph} title={labels.moved}>
              <Icon id="arrow-right-left" size={kiosk ? 24 : 16} label={labels.moved} />
            </span>
          ) : null}
          {changed ? <span class={styles.changed}>{labels.changed}</span> : null}
          {cancelled ? <span class={styles.cancelled}>{labels.cancelled}</span> : null}
        </span>
        {location ? <span class={styles.meta}>{location}</span> : null}
        {(members.length > 0 && !alone) || driver ? (
          <span class={styles.people}>
            {alone ? null : everyone ? (
              <span class={styles.chipSlot}>
                <MemberChip
                  name={labels.everyone}
                  colorKey={NEUTRAL_COLOR}
                  icon="family"
                  size={kiosk ? 'kiosk' : 'sm'}
                  plain={!kiosk}
                  {...(tone === 'past' ? { tone: 'muted' as const } : {})}
                />
              </span>
            ) : (
              members.map((m, i) => (
                <span key={i} class={styles.chipSlot}>
                  <MemberChip
                    name={m.name}
                    colorKey={m.colorKey}
                    icon={m.icon ?? null}
                    size={kiosk ? 'kiosk' : 'sm'}
                    {...(tone === 'past' ? { tone: 'muted' as const } : {})}
                    showName={!kiosk || members.length <= 2}
                  />
                </span>
              ))
            )}
            {driver === 'missing' ? (
              <span class={styles.chipSlot}>
                <span class={styles.driverMissing}>
                  <Icon id="car" size={kiosk ? 24 : 16} />
                  <span class={styles.driverText}>{labels.whoDrives}</span>
                </span>
              </span>
            ) : driver ? (
              <span class={cx(styles.chipSlot, styles.driver)}>
                <Icon id="car" size={kiosk ? 24 : 16} />
                <span class={styles.driverText}>
                  {driverText || labels.drives.replace('{name}', driver.name)}
                </span>
              </span>
            ) : null}
          </span>
        ) : null}
      </span>
    </>
  );
  const cls = cx(
    styles.card,
    kiosk ? styles.kiosk : styles.web,
    tone === 'next' && styles.next,
    tone === 'past' && styles.past,
    cancelled && styles.isCancelled,
    onPress && styles.pressable,
  );
  return onPress ? (
    <button type="button" class={cls} data-name={name} onClick={onPress}>
      {body}
    </button>
  ) : (
    <div class={cls} data-name={name}>
      {body}
    </div>
  );
}
